<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import LanguagesRepository from '../../repositories/languages.repository';
import Store from '@store/dataProvider';
import { storeToRefs } from 'pinia';
import { IPagination } from '../../types/pagination.type';
import { ILanguage } from '../../types/languages.type';
import { useToast } from '../../composables/useToast';
import { useI18n } from 'vue-i18n'
import CountriesRepository from '@repositories/countries.repository';
import { ICountry } from '../../types/countries.type';
import { getOptionsLanguages } from '../../helpers/lang/i18n';
import DataTable from '@components/DataTable.vue';

const isLoadingLanguages = ref<boolean>(false);

const { t } = useI18n();
const store = Store();
const { $toast } = useToast();
const { languages: languagesPinia }: any = storeToRefs(store);
const countriesData = ref<ICountry[]>([]);
const languages = ref<ILanguage[]>([]);
const currentPage = ref<number>();
const paginationData = ref<IPagination>();
const modalType = ref<string | null>(null);
const modalTitle = ref<string | undefined>();
const isModalOpen = ref<boolean>(false);
const selectedLanguage = ref<ILanguage | null>(null);
const optionsLanguage = ref<any>(null);

const fieldsTableActive: any = ref([
  {
    key: 'id',
    label: t('global.id'),
    sortable: true,
  },
  {
    key: 'language',
    label: t('ajustes.idiomas.idioma'),
  },
  {
    key: 'fileJson',
    label: t('ajustes.idiomas.archivo_json'),
  },
  {
    key: 'urlDomain',
    label: t('ajustes.idiomas.url_de_dominio'),
  },
//   {
//     key: 'country',
//     label: t('global.pais'),
// 	formatter: (e: any) => e?.description || null,
//   },
  {
    key: 'languageInfo',
    label: t('global.editar'),
  },
]);

// Load Languages ABM
const getLanguagesFromAPI = async (page: number, deleted: boolean = false) => {
  isLoadingLanguages.value = true;
  try {
    const { data } = await LanguagesRepository.getlanguages({ page, deleted });
    if (data && Array.isArray(data?.languages)) {
      paginationData.value = data.pagination;
      return data.languages;
    }
    return [];
  } catch (error) {
    console.error('Error while fetching languages:', error);
    return [];
  } finally {
    isLoadingLanguages.value = false;
  }
};

const updateLanguages = (newLanguages: ILanguage[]) => {
  languages.value = newLanguages;
  store.setProp({ key: 'languages', value: newLanguages });
};

const loadLanguages = async () => {
  const newLanguages = await getLanguagesFromAPI(1);
  updateLanguages(newLanguages);
  const dataCountries = await CountriesRepository.getcountries();

  countriesData.value = dataCountries.data.countries.map((e: ICountry) => ({
    id: e.id,
    nombre: e.description,
  }));

  optionsLanguage.value = getOptionsLanguages();
};

onMounted(loadLanguages);

// Create inputs from modals
const fieldsInputLanguage = computed(() => {
  return [
    {
      key: 'language',
      title: t('ajustes.idiomas.idioma'),
      type: 'text',
      validations: {
        rules: {
          required: true,
        },
		messages: {
		  required: t('global.es_obligatorio'),
		},
      },
      placeholder: t('ajustes.idiomas.idioma'),
      value: selectedLanguage.value?.language,
      cols: 12,
    },
    {
      key: 'fileJson',
      title: t('ajustes.idiomas.archivo_json'),
      type: 'select',
	  selectLabel: null,
	  options: optionsLanguage,
      validations: {
        rules: {
          required: true,
        },
		messages: {
		  required: t('global.es_obligatorio'),
		},
      },
      placeholder: t('ajustes.idiomas.archivo_json'),
      value: selectedLanguage.value?.fileJson,
      cols: 12,
    },
    {
      key: 'urlDomain',
      title: t('ajustes.idiomas.url_de_dominio'),
      type: 'text',
      validations: {
        rules: {
          required: true,
          verifyExpreRegular: /^(ftp|http|https):\/\/[^ "]+(?<!\/)$/,
        },
		messages: {
		  required: t('global.es_obligatorio'),
		},
      },
      placeholder: 'https://example.com',
      value: selectedLanguage.value?.urlDomain,
      cols: 12,
    },
	// {
    //  key: 'countryId',
    //  title: t('global.pais'),
    //  selectLabel: null,
    //  type: 'select',
    //  placeholder: t('global.pais'),
    //  value: selectedLanguage.value?.countryId,
    //  options: countriesData,
    //  validations: {
    //    rules: {
    //      required: true,
    //    },
	//    messages: {
	// 	  required: t('global.es_obligatorio'),
	//    },
    //  },
  	// },
  ];
});

// UI
const onChangePage = async (page: number) => {
  getLanguagesFromAPI(page);
  currentPage.value = page;
};

const handleOpenModalCreate = () => {
  modalTitle.value = t('ajustes.idiomas.agregar_idioma');
  isModalOpen.value = true;
  modalType.value = 'modalCreateLanguage';
};

const handleClose = () => {
  modalTitle.value = undefined;
  isModalOpen.value = false;
  modalType.value = null;
  selectedLanguage.value = null;
};

//CRUD
const handleCreateLanguage = async ({ items, isFormValid }: any) => {
  if (!isFormValid) return;
  const languageForCreate = {
    ...items,
	// countryId: items.countryId.id,
	fileJson: items.fileJson.nombre
  };
  try {
    const data = await LanguagesRepository.createLanguage(languageForCreate);
    store.setProp({ key: 'languages', value: [...languages.value, data] });
    $toast?.success(t('ajustes.idiomas.pais_agregado_con_exito'));
  } catch (error) {
    $toast?.error(t('global.ocurrio_un_error'));
  }

  handleClose();
};

const handleSelectLanguage = (languageData: ILanguage) => {
  modalTitle.value = t('ajustes.idiomas.actualizar_pais');
  (isModalOpen.value = true), (modalType.value = 'modalUpdateLanguage');
  selectedLanguage.value = languageData;
};

const handleUpdateLanguage = async ({ items, isFormValid }: any) => {
  if (!isFormValid || !selectedLanguage.value) return;
  const languageForUpdate = {
    ...items,
    id: selectedLanguage.value.id,
	// countryId: items.countryId.id,
	fileJson: items.fileJson.nombre
  };

  try {
    const data = await LanguagesRepository.updateLanguage(languageForUpdate);
    let index = store.languages.findIndex((value: any) => value.id === data.id);
    if (index > -1) {
      store.setProp({ key: 'languages', index, value: data });
    }
    $toast?.success(t('ajustes.idiomas.pais_actualizado_con_exito'));
  } catch (error) {
    $toast?.error(t('global.ocurrio_un_error'));
  }

  handleClose();
};

</script>

<template>
  <Layout :title="t('ajustes.idiomas.idiomas')" :textColor="'color-secondary'">
    <template #layout>
      <div class="d-flex justify-content-end">
        <CButton
          class="background-pink button__form"
          style="padding: 0.5rem !important"
          type="button"
          @click="handleOpenModalCreate"
        >
          <template #content>{{t('ajustes.idiomas.agregar_idioma')}}</template>
        </CButton>
      </div>

      <DataTable
        :loading="isLoadingLanguages"
        :fields="fieldsTableActive"
        :items="languagesPinia"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-slot:cell(languageInfo)="languageData">
          <div
            @click="handleSelectLanguage(languageData.data.item)"
            class="button-icon"
          >
            <img src="/icons/editar.svg" />
          </div>
        </template>
      </DataTable>

      <BModal
        :modelValue="isModalOpen"
        hide-footer
        :centered="true"
        @hide="handleClose"
        static
        :title="modalTitle"
      >
        <template v-if="modalType === 'modalCreateLanguage'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputLanguage"
            @onSubmit="handleCreateLanguage"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>{{t('ajustes.idiomas.agregar_idioma')}}</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>

        <template v-if="modalType === 'modalUpdateLanguage'">
          <Form
            ref="refTableForm"
            :inputs="fieldsInputLanguage"
            @onSubmit="handleUpdateLanguage"
          >
            <template #buttons>
              <div class="d-flex">
                <CButton class="w-100 fs-6 p-2" background="background-blue">
                  <template #content>
                    <div>
                      <span>{{t('global.actualizar')}}</span>
                    </div>
                  </template>
                </CButton>
              </div>
            </template>
          </Form>
        </template>
      </BModal>
    </template>
  </Layout>
</template>
